import { ChangeEvent, useState } from "react"
import { Button, Form, Modal } from 'react-bootstrap'
import toast from "react-hot-toast"
import isEmail from "validator/es/lib/isEmail"

import LoadingTextSwap from "../../../components/LoadingTextSwap"
import { useChangeClubTeamMemberMutation, useFetchUsersQuery } from "../../../app/adminApiSlice"
import findUser from "../../../utils/findUser"
import getPosition from "../../../utils/getPosition"

interface ChangeClubTeamMemberProps {
  clubId: string,
  position: string,
  staff: Record<string, { id: string, name: string }>,
  children: React.ReactNode
}

const ChangeClubTeamMember = ({ clubId, position, staff, children }: ChangeClubTeamMemberProps) => {
  const { data: users, isLoading: isLoadingUsers } = useFetchUsersQuery()
  const [changeClubTeamMember, { isLoading }] = useChangeClubTeamMemberMutation()

  const [show, setShow] = useState(false)
  const [newEmail, setNewEmail] = useState("")
  const [error, setError] = useState("")

  const handleShow = () => setShow(true)
  const handleClose = () => {
    setShow(false)
    setNewEmail("")
    setError("")
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value)
    setError("")
  }

  const pos = getPosition(position)

  const handleSubmit = async () => {
    if (error) return
    if (isLoadingUsers || !users) return

    try {
      if (!newEmail || !isEmail(newEmail)) return setError("Please enter a valid email")

      // Check if user exists
      const result = findUser(newEmail, users)
      if (!result) return setError("Could not find user")
      const { userId } = result

      // Check user doesnt already have that position
      if (staff[position].id === userId) return setError("User already has that position")

      await changeClubTeamMember({ clubId, userId, position }).unwrap()

      handleClose()
      toast.success(`${ pos } updated`)

    } catch (error) {
      console.log(error)

      if (error.status === "functions/failed-precondition") {
        setError(error.data.message)
        return
      }
      toast.error("Error updating database")
      handleClose()
    }
  }

  return (
    <>
      <div onClick={handleShow}>{children}</div>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header className="border-0 p-0 mb-3">
          <Modal.Title className="fs-5 fw-bold">Change {pos}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="mb-3 p-0">
          <p className="p-0 text-muted fs-7 mb-3">Please choose a new {pos.toLowerCase()}</p>
          <Form onSubmit={e => { e.preventDefault(); handleSubmit() }}>
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                placeholder="Enter new email"
                value={newEmail}
                onChange={handleChange}
                isInvalid={!!error}
                autoFocus
              />
              <Form.Control.Feedback type="invalid" className="fw-semibold">
                <i>{error}</i>
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="p-0 border-0">
          <Button disabled={isLoading} className="shadow-sm rounded-3 m-0 fs-7 me-2" variant="dark" onClick={handleSubmit}>
            <LoadingTextSwap isLoading={isLoading}>
              Save
            </LoadingTextSwap>
          </Button>
          <Button disabled={isLoading} className="shadow-btn rounded-3 border-1 m-0 border-light fs-7" variant="" onClick={handleClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ChangeClubTeamMember